import type { TOC } from '@ember/component/template-only';
import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import {
  PrismBadge,
  PrismBanner,
  PrismButton,
  PrismComboSelectInput,
  PrismDrawer,
  PrismIcon,
  PrismModal,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, or } from 'ember-truth-helpers';
import { local } from 'embroider-css-modules';
import GeneralDatepickersDatepickerWithSidebar from 'lumen/components/general/datepickers/datepicker-with-sidebar';
import GeneralFilterBarFilterBar from 'lumen/components/general/filter-bar/filter-bar';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import HotelOverviewDashboardFeedDashboardFeed from 'lumen/components/hotel/overview-dashboard/feed/dashboard-feed';
import HotelOverviewDashboardFiltersRateFilters from 'lumen/components/hotel/overview-dashboard/filters/rate-filters';
import HotelOverviewDashboardModalsAddKeyDate from 'lumen/components/hotel/overview-dashboard/modals/add-key-date';
import HotelOverviewDashboardTilesDistributionHealthRankingAndParityTile from 'lumen/components/hotel/overview-dashboard/tiles/distribution-health/ranking-and-parity-tile';
import HotelOverviewDashboardTilesMarketTrendsDemandLevel from 'lumen/components/hotel/overview-dashboard/tiles/market-trends/demand-level';
import HotelOverviewDashboardTilesMarketTrendsEvents from 'lumen/components/hotel/overview-dashboard/tiles/market-trends/events';
import HotelOverviewDashboardTilesMarketTrendsPriceDifferenceAggregatedView from 'lumen/components/hotel/overview-dashboard/tiles/market-trends/price-difference-aggregated-view';
import HotelOverviewDashboardTilesMarketTrendsPriceDifferenceDailyView from 'lumen/components/hotel/overview-dashboard/tiles/market-trends/price-difference-daily-view';
import HotelOverviewDashboardTilesMarketTrendsTopSearchingCountries from 'lumen/components/hotel/overview-dashboard/tiles/market-trends/top-searching-countries';
import HotelOverviewDashboardTilesTopInsightsEmptyStateOccupancy from 'lumen/components/hotel/overview-dashboard/tiles/top-insights/empty-state-occupancy';
import HotelOverviewDashboardTilesTopInsightsMyAdr from 'lumen/components/hotel/overview-dashboard/tiles/top-insights/my-adr';
import HotelOverviewDashboardTilesTopInsightsMyRevenue from 'lumen/components/hotel/overview-dashboard/tiles/top-insights/my-revenue';
import HotelOverviewDashboardTilesTopInsightsOccupancy from 'lumen/components/hotel/overview-dashboard/tiles/top-insights/occupancy';
import HotelOverviewDashboardTilesTopInsightsPerformanceRatesTabContainer from 'lumen/components/hotel/overview-dashboard/tiles/top-insights/performance-rates-tab-container';
import HotelAndMarketDropdownsAccountSelectorWrapper from 'lumen/components/hotel-and-market/dropdowns/account-selector-wrapper';
import type HotelOverviewController from 'lumen/controllers/hotel/overview';
import dayjsFormat from 'lumen/helpers/dayjs-format';
import isDrawerExpanded from 'lumen/helpers/is-drawer-expanded';
import isOdTileVisible from 'lumen/helpers/is-od-tile-visible';
import {
  DRAWER_IDS,
  FILTER_BAR_PAGETYPES,
  HEADER_HEIGHT,
} from 'lumen/utils/constants/general-constants';
import {
  OD_DEFAULT_DATE_FORMAT,
  OD_SELECTED_TILES_IDS,
} from 'lumen/utils/constants/overview-dashboard-constants';

import styles from './overview.module.css';

interface HotelOverviewSignature {
  Args: {
    controller: HotelOverviewController;
    model: unknown;
  };
}

<template>
  {{! @glint-nocheck: Need to convert controller to *.ts }}
  <GeneralPageHeader>
    <div class="content-wrapper flex flex-row items-center">
      <div class="flex gap-400">
        <HotelAndMarketDropdownsAccountSelectorWrapper @hasDivider={{true}} />
        <div class="mr-200">
          <GeneralDatepickersDatepickerWithSidebar
            @displayEvents={{false}}
            @maxDate={{@controller.datePickerMaxDate}}
            @maxRange="1 year"
            @minDate={{@controller.datePickerMinDate}}
            @range={{@controller.datePickerRange}}
            @rangeChanged={{@controller.rangeChanged}}
            @selectSingleDay={{false}}
            @sidebarLists={{@controller.datePickerRangeOptions}}
            class="ic-product-tour-hotel-overview-datepicker"
          />
        </div>
      </div>
      <div class="flex flex-row items-center gap-200">
        {{! Vs. X days ago reference selection }}
        <PrismSelectInput
          @onChange={{@controller.onRefDaysAgoSelectionChanged}}
          @options={{@controller.refDaysAgoSelectionOptions}}
          @unsafe_isClampedToOneLine={{true}}
          @value={{@controller.selectedRefDaysAgo}}
        />

        {{#if @controller.mappedActiveFilters}}
          <GeneralFilterBarFilterBar
            @activeFilters={{@controller.mappedActiveFilters}}
            @filterBarSegmentationClicked={{@controller.clearActiveFilter}}
            @hideFavourites={{true}}
            @pageType={{FILTER_BAR_PAGETYPES.OVERVIEW_DASHBOARD}}
          />
        {{/if}}
      </div>

      <HotelOverviewDashboardFiltersRateFilters
        @otaId={{@controller.otaId}}
        @rateParam={{@controller.rateParam}}
        @selectOta={{@controller.selectOta}}
        @selectRateType={{@controller.selectRateType}}
        @setCompsetId={{@controller.setCompsetId}}
      />

      {{#unless @controller.access.hasSmartInsights}}
        <div
          class="ic-product-tour-hotel-overview-settings-dropdown ml-400 flex items-center justify-center"
        >
          <PrismComboSelectInput
            @horizontalPosition="right"
            @onChange={{@controller.onSettingsSelect}}
            @options={{@controller.odSettingsOptions}}
            @value={{@controller.odSettingsValue}}
            class="ic-product-tour-hotel-overview-in-page-settings-trigger"
          />
        </div>
      {{/unless}}

    </div>
  </GeneralPageHeader>

  <GeneralPageMain
    {{didInsert @controller.initODSelectionsService}}
    {{didInsert @controller.setTopInsightDefaultChartTab}}
  >
    <div class="content-wrapper main-content">
      {{#if
        (and
          @controller.alertsResponseData
          @controller.formattedDataFetchErrorMessage
        )
      }}
        <div class="mb-800">
          <PrismBanner
            @bannerStyle="warning"
            @bannerTitle={{@controller.formattedDataFetchErrorMessage}}
          />
        </div>
      {{/if}}

      {{#if @controller.odAccess.hasAtLeastOneTopInsightsTile}}
        <section class={{styles.od-dashboard-section}}>
          <div class="section-title mt-0 flex items-center justify-between">
            <h2 class="text-neutral-emphasis">{{t
                "overview_dashboard.header.insights"
              }}
              <span class="text-200--regular">
                {{@controller.selectedRefDaysAgo.label}}
              </span>
            </h2>
            <p class="text-200--regular text-neutral-subdued ml-300">
              {{#if @controller.odSelections.isDayDrilldownActive}}
                {{dayjsFormat
                  @controller.odSelections.activeDrilldownDayAsString
                  format=OD_DEFAULT_DATE_FORMAT
                }}
              {{else}}
                {{@controller.formattedDateRangeForKpisWithPastDataAvailable}}
              {{/if}}
            </p>
          </div>

          <div class={{styles.od-dashboard-kpi-grid-layout}}>
            {{#if
              (or
                (isOdTileVisible OD_SELECTED_TILES_IDS.my_otb)
                (isOdTileVisible OD_SELECTED_TILES_IDS.market_otb)
              )
            }}
              <HotelOverviewDashboardTilesTopInsightsOccupancy
                @compsetId={{@controller.compsetId}}
                @data={{@controller.otbDataWithDaySelectionIntoAccount}}
                @isLoading={{or
                  @controller.model.otbDataRequestTask.isRunning
                  (and
                    @controller.access.hasShortTermRentalAccess
                    @controller.model.rentalDataRequestTask.isRunning
                  )
                }}
                @marketOtbUpdateIndicatorContent={{@controller.marketOtbUpdateIndicatorContent}}
                @otaId={{@controller.otaId}}
                @otbUpdateIndicatorContent={{@controller.otbUpdateIndicatorContent}}
                @rateParam={{@controller.rateParam}}
                @rentalData={{@controller.rentalData}}
                @selectedRange={{@controller.datePickerRange}}
                @selectedRefDaysAgo={{@controller.selectedRefDaysAgo}}
                @tileId={{OD_SELECTED_TILES_IDS.occupancy}}
              />
            {{else if
              @controller.odAccess.isOtbSelfServiceOccupancyEntryPointVisible
            }}
              <HotelOverviewDashboardTilesTopInsightsEmptyStateOccupancy />
            {{/if}}

            {{#if (isOdTileVisible OD_SELECTED_TILES_IDS.my_adr)}}
              <HotelOverviewDashboardTilesTopInsightsMyAdr
                @data={{@controller.revenueDataWithDaySelectionIntoAccount}}
                @isLoading={{@controller.model.revenueDataRequestTask.isRunning}}
                @selectedRange={{@controller.datePickerRange}}
                @selectedRefDaysAgo={{@controller.selectedRefDaysAgo}}
                @tileId={{OD_SELECTED_TILES_IDS.my_adr}}
              />
            {{/if}}

            {{#if (isOdTileVisible OD_SELECTED_TILES_IDS.my_revenue)}}
              <HotelOverviewDashboardTilesTopInsightsMyRevenue
                @data={{@controller.revenueDataWithDaySelectionIntoAccount}}
                @isLoading={{@controller.model.revenueDataRequestTask.isRunning}}
                @selectedRange={{@controller.datePickerRange}}
                @selectedRefDaysAgo={{@controller.selectedRefDaysAgo}}
                @tileId={{OD_SELECTED_TILES_IDS.my_revenue}}
              />
            {{/if}}

            {{#if
              (or
                (isOdTileVisible OD_SELECTED_TILES_IDS.stay_date_graph)
                (isOdTileVisible OD_SELECTED_TILES_IDS.pricing_difference)
              )
            }}
              <HotelOverviewDashboardTilesTopInsightsPerformanceRatesTabContainer
                @dailyPatternsOtbData={{@controller.dailyPatternsOtbData}}
                @dailyPatternsPriceOpportunityData={{@controller.dailyPatternsPriceOpportunityData}}
                @dailyPatternsPricePositionData={{@controller.dailyPatternsPricePositionData}}
                @dailyPatternsUpdatedIndicatorContent={{@controller.dailyPatternsUpdatedIndicatorContent}}
                @datePickerRange={{@controller.datePickerRange}}
                @isDailyPatternsLoading={{@controller.isDailyPatternsDataLoading}}
                @isDailyPatternsUpdateIndicatorVisible={{@controller.isDailyPatternsUpdateIndicatorVisible}}
                @isRatesLoading={{@controller.isRatesDataLoading}}
                @numberOfCompetitors={{@controller.numberOfCompetitors}}
                @otaId={{@controller.otaId}}
                @otbData={{@controller.otbData}}
                @priceDifferenceData={{@controller.priceDifferenceData}}
                @rangeChanged={{@controller.rangeChanged}}
                @rentalData={{@controller.rentalData}}
                @selectedRefDaysAgo={{@controller.selectedRefDaysAgo}}
                @selectedTab={{@controller.topInsightChartTab}}
                @toggleOrSetSelectedDay={{@controller.toggleOrSetSelectedDay}}
                @updateTopInsightChartTab={{@controller.updateTopInsightChartTab}}
              />
            {{/if}}
          </div>
        </section>
      {{/if}}

      {{#if @controller.odAccess.hasAtLeastOneMarketTrendsTile}}
        <section class={{styles.od-dashboard-section}}>
          <div class="section-title flex items-center justify-between">
            <h2 class="text-neutral-emphasis mr-200 flex items-center">{{t
                "overview_dashboard.header.market_trends"
              }}</h2>
            <p class="text-200--regular text-neutral-subdued ml-300">
              {{#if @controller.odSelections.isDayDrilldownActive}}
                {{dayjsFormat
                  @controller.odSelections.activeDrilldownDayAsString
                  format=OD_DEFAULT_DATE_FORMAT
                }}
              {{else}}
                {{@controller.formattedDateRangeForKpisWithForwardLookingData}}
              {{/if}}
            </p>
          </div>
          {{#if
            (or
              @controller.odSelections.isDayDrilldownActive
              @controller.odSelections.isSingleDaySelectedInTheDatepicker
            )
          }}
            {{#if
              @controller.marketDemandDataWithDaySelectionIntoAccount.hasOpportunity
            }}
              <div class="mb-400">
                <PrismBanner
                  @bannerMessage={{t
                    @controller.marketDemandDataWithDaySelectionIntoAccount.formattedOutlierMessage.translationKeyMessage
                  }}
                  @bannerStyle="warning"
                  @bannerTitle="{{t
                    'overview_dashboard.market.price_opportunity'
                  }} - {{t
                    @controller.marketDemandDataWithDaySelectionIntoAccount.formattedOutlierMessage.translationKeyTitle
                  }}"
                  @hasCta={{true}}
                  @primaryAction={{@controller.marketDemandHasOpportunityPrimaryAction}}
                />
              </div>
            {{/if}}
          {{/if}}

          <div class={{styles.od-dashboard-kpi-grid-layout}}>
            {{#if (isOdTileVisible OD_SELECTED_TILES_IDS.demand_level)}}
              <HotelOverviewDashboardTilesMarketTrendsDemandLevel
                @data={{@controller.marketDemandDataWithDaySelectionIntoAccount}}
                @isLoading={{@controller.model.marketDemandDataRequestTask.isRunning}}
                @selectedRange={{@controller.datePickerRange}}
                @tileId={{OD_SELECTED_TILES_IDS.demand_level}}
              />
              {{#if @controller.isTopSearchingCountriesAvailable}}
                <HotelOverviewDashboardTilesMarketTrendsTopSearchingCountries
                  @data={{@controller.marketDemandDataWithDaySelectionIntoAccount}}
                  @isLoading={{@controller.model.marketDemandDataRequestTask.isRunning}}
                  @selectedRange={{@controller.datePickerRange}}
                  @tileId={{OD_SELECTED_TILES_IDS.top_searching_countries}}
                />
              {{/if}}
            {{/if}}

            {{#if (isOdTileVisible OD_SELECTED_TILES_IDS.pricing_difference)}}
              {{#if
                (or
                  @controller.odSelections.isDayDrilldownActive
                  @controller.odSelections.isSingleDaySelectedInTheDatepicker
                )
              }}
                <HotelOverviewDashboardTilesMarketTrendsPriceDifferenceDailyView
                  @compsetId={{@controller.compsetId}}
                  @data={{@controller.priceDifferenceDataWithSingleDaySelectionIntoAccount}}
                  @isLoading={{@controller.model.priceDifferenceDataRequestTask.isRunning}}
                  @otaId={{@controller.otaId}}
                  @rateParam={{@controller.rateParam}}
                  @selectedRange={{@controller.datePickerRange}}
                  @selectedRefDaysAgo={{@controller.selectedRefDaysAgo}}
                  @tileId={{concat
                    OD_SELECTED_TILES_IDS.pricing_difference
                    "_daily"
                  }}
                />
              {{else}}
                <HotelOverviewDashboardTilesMarketTrendsPriceDifferenceAggregatedView
                  @compsetId={{@controller.compsetId}}
                  @data={{@controller.priceDifferenceData}}
                  @isLoading={{@controller.model.priceDifferenceDataRequestTask.isRunning}}
                  @otaId={{@controller.otaId}}
                  @rateParam={{@controller.rateParam}}
                  @selectedRange={{@controller.datePickerRange}}
                  @selectedRefDaysAgo={{@controller.selectedRefDaysAgo}}
                  @tileId={{concat
                    OD_SELECTED_TILES_IDS.pricing_difference
                    "_aggregated"
                  }}
                />
              {{/if}}
            {{/if}}

            {{#if (isOdTileVisible OD_SELECTED_TILES_IDS.events)}}
              <HotelOverviewDashboardTilesMarketTrendsEvents
                @data={{@controller.combinedDataForEventsTile}}
                @isAlertsRequestLoading={{@controller.model.requestAlertsDataTask.isRunning}}
                @isLoading={{@controller.isDataForEventsTileLoading}}
                @onKeyDateStarClicked={{@controller.onKeyDateStarClicked}}
                @onSelectDayForDayDrilldown={{@controller.updateSelectedDay}}
                @relaunchAlertsDataRequest={{@controller.relaunchAlertsDataRequest}}
                @selectedRange={{@controller.datePickerRange}}
                @selectedRefDaysAgo={{@controller.selectedRefDaysAgo}}
                @tileId={{OD_SELECTED_TILES_IDS.events}}
              />
            {{/if}}
          </div>
        </section>
      {{/if}}

      {{#if @controller.odAccess.hasAtLeastOneDistributionHealthTile}}
        <section class={{styles.od-dashboard-section}}>
          <div class="section-title flex items-center justify-between">
            <h2 class="text-neutral-emphasis mr-200 flex items-center">{{t
                "overview_dashboard.header.distribution"
              }}</h2>
          </div>

          <PrismBanner
            @bannerMessage={{t "overview_dashboard.banner.distribution.title"}}
          />

          <div
            class={{local
              styles
              "od-dashboard-kpi-grid-layout"
              "od-dashboard-kpi-grid-layout--ranking-section"
            }}
          >
            {{#each @controller.availableRankingAndParityOtaIds as |otaId|}}
              <HotelOverviewDashboardTilesDistributionHealthRankingAndParityTile
                @allParityData={{@controller.parityData}}
                @allRankingData={{@controller.rankingData}}
                @compsetId={{@controller.compsetId}}
                @isLoading={{@controller.isRankingAndParityDataLoading}}
                @otaId={{otaId}}
                @rateParam={{@controller.rateParam}}
                @selectedRange={{@controller.datePickerRange}}
                @tileId={{OD_SELECTED_TILES_IDS.distribution}}
              />
            {{/each}}
          </div>
        </section>
      {{/if}}
    </div>
  </GeneralPageMain>

  {{#unless @controller.access.hasSmartInsights}}
    <PrismDrawer
      @contentPadding="none"
      @isOpen={{isDrawerExpanded DRAWER_IDS.OD_DASHBOARD_FEED}}
      @onToggle={{@controller.onDashboardFeedDrawerToggle}}
      @positionTop={{HEADER_HEIGHT}}
    >
      <:trigger>
        <div
          class="od-drawer-trigger-container flex items-center justify-center"
        >
          <div class="od-drawer-trigger-content">
            <div class="od-drawer-trigger-content-icon">
              <PrismIcon @color="default-inverse" @name="notification" />
            </div>

            {{! Badge }}
            <div class="badge-wrapper">
              {{#if @controller.model.requestAlertsDataTask.isRunning}}
                <PrismBadge
                  @isSmall="true"
                  @label="..."
                  @type="unsafe_critical"
                />
              {{else}}
                {{#if @controller.dashboardFeedData.length}}
                  <PrismBadge
                    @isSmall="true"
                    @label={{@controller.dashboardFeedData.length}}
                    @type="unsafe_critical"
                  />
                {{/if}}
              {{/if}}
            </div>
            {{! /Badge }}
          </div>
        </div>
      </:trigger>
      <:content>
        <div class={{styles.od-drawer}}>
          <div class={{styles.od-drawer__header}}>
            <div class="mb-400 flex flex-row items-center justify-between">
              <h3>{{t "overview_dashboard.header.my_highlights"}}</h3>

              <button
                class="button--unstyled"
                type="button"
                {{on "click" @controller.onDashboardFeedDrawerToggle}}
              >
                <PrismIcon @name="cross" @size="small" />
              </button>
            </div>

            <div class={{styles.od-drawer__filters}}>
              <div class={{styles.drawer-filter}}>
                <div class={{styles.drawer-filter-trigger}}>
                  <PrismSelectInput
                    @horizontalPosition="right"
                    @onChange={{@controller.dashboardFeedShowTypeChanged}}
                    @options={{@controller.dashboardFeedShowFilterOptions}}
                    @value={{@controller.dashboardFeedSelectedShowType}}
                  />
                </div>
              </div>

              <div class={{styles.drawer-filter}}>
                <div class={{styles.drawer-filter-trigger}}>
                  <PrismComboSelectInput
                    @buildLabelWithCount={{@controller.dashboardFeedAlertTypesBuildLabelWithCount}}
                    @horizontalPosition="right"
                    @onChange={{@controller.dashboardFeedAlertTypesChanged}}
                    @options={{@controller.dashboardFeedTypesFilterOptions}}
                    @selectAllLabel={{t
                      "overview_dashboard.feed.types_filter.types.count.any"
                    }}
                    @value={{@controller.dashboardFeedTypesFilterValue}}
                  />
                </div>
              </div>

              <div class={{styles.drawer-filter}}>
                <div class={{styles.drawer-filter-trigger}}>
                  <PrismSelectInput
                    @horizontalPosition="right"
                    @onChange={{@controller.dashboardFeedSortTypeChanged}}
                    @options={{@controller.dashboardFeedSortOptions}}
                    @value={{@controller.dashboardFeedSelectedSortType}}
                  />
                </div>
              </div>
            </div>
          </div>

          <div class={{styles.od-drawer__content}}>
            <HotelOverviewDashboardFeedDashboardFeed
              @alertsResponseData={{@controller.alertsResponseData}}
              @dashboardFeedData={{@controller.dashboardFeedData}}
              @dashboardFeedSelectedShowType={{@controller.dashboardFeedSelectedShowType}}
              @hasShowFilterValueChanged={{@controller.hasShowFilterValueChanged}}
              @hasTypesFilterValueChanged={{@controller.hasTypesFilterValueChanged}}
              @isAlertsRequestLoading={{@controller.model.requestAlertsDataTask.isRunning}}
              @isCardFooterVisible={{@controller.isDrawerCardFooterVisible}}
              @numberOfCompetitors={{@controller.numberOfCompetitors}}
              @onKeyDateStarClicked={{@controller.onKeyDateStarClicked}}
              @openAddKeyDateModal={{@controller.openAddKeyDateModal}}
              @relaunchAlertsDataRequest={{@controller.relaunchAlertsDataRequest}}
              @toggleOrSetSelectedDay={{@controller.toggleOrSetSelectedDay}}
              @triggerDayDrilldownOutsideSelectedRange={{@controller.triggerDayDrilldownOutsideSelectedRange}}
              @updateKeyDateNoteTask={{@controller.updateKeyDateNoteTask}}
            />

          </div>
          {{#if @controller.isDrawerCardFooterVisible}}
            <div class={{styles.od-drawer__footer}}>
              <PrismButton
                @iconBefore="plus"
                @label={{t "overview_dashboard.key_date.button.add_key_date"}}
                @onClick={{@controller.openAddKeyDateModal}}
                class={{styles.drawer-footer-button}}
              />
            </div>
          {{/if}}
        </div>
      </:content>
    </PrismDrawer>
  {{/unless}}

  {{#if @controller.isShowingAddKeyDateModal}}
    <PrismModal
      @onClose={{@controller.closeAddKeyDateModal}}
      @title={{t "overview_dashboard.key_date.modal.header"}}
    >
      <HotelOverviewDashboardModalsAddKeyDate
        @alertsResponseData={{@controller.alertsResponseData}}
        @closeModal={{@controller.closeAddKeyDateModal}}
        @createKeyDateTask={{@controller.createKeyDateTask}}
        @onSelectDate={{@controller.onSelectDate}}
        @relaunchAlertsDataRequest={{@controller.relaunchAlertsDataRequest}}
      />
    </PrismModal>
  {{/if}}

  {{outlet}}
</template> satisfies TOC<HotelOverviewSignature>;
